import {
  useEffect,
  useId,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { IconButton } from "./Button";
import { cx } from "../utils/cx";

export interface ModalSurfaceProps {
  open: boolean;
  title: string;
  description?: string | undefined;
  children: ReactNode;
  footer?: ReactNode | undefined;
  onClose: () => void;
  closeLabel?: string | undefined;
  initialFocusRef?: RefObject<HTMLElement | null> | undefined;
  variant: "dialog" | "drawer";
  drawerPosition?: "left" | "right" | undefined;
  className?: string | undefined;
}

const focusableSelector = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const modalStack: string[] = [];
let bodyOverflowBeforeModals: string | undefined;

function syncModalEnvironment() {
  if (typeof document === "undefined") {
    return;
  }
  const topId = modalStack.at(-1);
  const backdrops = document.querySelectorAll<HTMLElement>(
    "[data-ui-modal-id]",
  );
  for (const backdrop of backdrops) {
    const isTop = backdrop.dataset["uiModalId"] === topId;
    backdrop.inert = !isTop;
    if (isTop) {
      backdrop.removeAttribute("aria-hidden");
    } else {
      backdrop.setAttribute("aria-hidden", "true");
    }
  }
  for (const child of Array.from(document.body.children)) {
    if (child instanceof HTMLElement && !child.matches("[data-ui-modal-id]")) {
      child.inert = modalStack.length > 0;
    }
  }
}

export function ModalSurface({
  open,
  title,
  description,
  children,
  footer,
  onClose,
  closeLabel = "Close",
  initialFocusRef,
  variant,
  drawerPosition = "right",
  className,
}: ModalSurfaceProps) {
  const titleId = useId();
  const descriptionId = useId();
  const modalId = useId();
  const surfaceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || typeof document === "undefined") {
      return;
    }
    const previouslyFocused = document.activeElement as HTMLElement | null;
    if (modalStack.length === 0) {
      bodyOverflowBeforeModals = document.body.style.overflow;
    }
    document.body.style.overflow = "hidden";
    modalStack.push(modalId);
    syncModalEnvironment();

    const frame = window.requestAnimationFrame(() => {
      const activeElement = document.activeElement as HTMLElement | null;
      const focusAlreadyInside =
        activeElement && surfaceRef.current?.contains(activeElement);
      const focusTarget = initialFocusRef?.current ??
        (focusAlreadyInside
          ? null
          : surfaceRef.current?.querySelector<HTMLElement>(focusableSelector) ??
            surfaceRef.current);
      focusTarget?.focus();
    });

    return () => {
      window.cancelAnimationFrame(frame);
      const index = modalStack.lastIndexOf(modalId);
      if (index >= 0) {
        modalStack.splice(index, 1);
      }
      syncModalEnvironment();
      if (modalStack.length === 0) {
        document.body.style.overflow = bodyOverflowBeforeModals ?? "";
        bodyOverflowBeforeModals = undefined;
      }
      const activeModalId = modalStack.at(-1);
      const focusOwner = previouslyFocused?.closest<HTMLElement>(
        "[data-ui-modal-id]",
      )?.dataset["uiModalId"];
      if (
        previouslyFocused?.isConnected &&
        (activeModalId === undefined || focusOwner === activeModalId)
      ) {
        previouslyFocused.focus();
      }
    };
  }, [initialFocusRef, modalId, open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape" && modalStack.at(-1) === modalId) {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [modalId, onClose, open]);

  if (!open || typeof document === "undefined") {
    return null;
  }

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Tab" || !surfaceRef.current) {
      return;
    }

    const focusable = Array.from(
      surfaceRef.current.querySelectorAll<HTMLElement>(focusableSelector),
    );
    if (focusable.length === 0) {
      event.preventDefault();
      surfaceRef.current.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  return createPortal(
    <div
      data-ui-modal-id={modalId}
      className={cx(
        "ui-modal-backdrop",
        `ui-modal-backdrop--${variant}`,
      )}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={surfaceRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cx(
          "ui-modal-surface",
          `ui-modal-surface--${variant}`,
          variant === "drawer" &&
            `ui-modal-surface--drawer-${drawerPosition}`,
          className,
        )}
        onKeyDown={handleKeyDown}
      >
        <div className="ui-modal-surface__header">
          <div>
            <h2 id={titleId}>{title}</h2>
            {description ? <p id={descriptionId}>{description}</p> : null}
          </div>
          <IconButton label={closeLabel} onClick={onClose}>
            ×
          </IconButton>
        </div>
        <div className="ui-modal-surface__body">{children}</div>
        {footer ? (
          <div className="ui-modal-surface__footer">{footer}</div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
